import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

import {
  getAutomationRules,
  deleteAutomationRule,
  toggleAutomationRule,
} from "api/apiService";
import { notifyCenter } from "utils/toast";

// Soft UI theme forces display:grid + placeItems:center on MuiInputBase-root,
// which shrinks the clickable area. Override so the whole Select opens the dropdown.
const SELECT_SX = {
  height: 40,
  "&.MuiInputBase-root": {
    display: "flex !important",
    placeItems: "initial !important",
    padding: "0 !important",
  },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center !important",
    minHeight: "40px !important",
    boxSizing: "border-box",
    padding: "0 32px 0 14px !important",
    flex: 1,
    width: "100% !important",
  },
};

// Soft UI's table CSS resets — restore standard table layout and add some
// breathing room. Same overrides used in /task-jobs.
const TABLE_SX = {
  width: "100%",
  minWidth: 1080,
  tableLayout: "fixed",
  "& thead": {
    display: "table-header-group",
    padding: 0,
    borderRadius: 0,
  },
  "& tbody": { display: "table-row-group" },
  "& tr": { display: "table-row" },
  "& td, & th": {
    display: "table-cell",
    padding: "12px 14px",
    borderBottom: (t) => `1px solid ${t.palette.grey[200]}`,
    fontSize: 13,
    verticalAlign: "middle",
  },
  "& thead th": {
    backgroundColor: (t) => t.palette.grey[100],
    color: (t) => t.palette.text.secondary,
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: 11,
    letterSpacing: 0.4,
    whiteSpace: "nowrap",
  },
  "& tbody tr:hover": {
    backgroundColor: (t) => t.palette.grey[50],
  },
};

const TRIGGER_LABELS = {
  event: "Event",
  schedule: "Tidsplan",
};

const ACTION_LABELS = {
  send_email: "E-post",
  send_vendor_push: "Vendor-push",
  push_to_wc: "Push til WC",
};

const ACTION_COLORS = {
  send_email: "info",
  send_vendor_push: "warning",
  push_to_wc: "secondary",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function describeTrigger(rule) {
  if (rule.triggerType === "event") {
    return rule.eventType || "—";
  }
  if (rule.triggerType === "schedule") {
    const off = Number(rule.scheduleOffsetMin || 0);
    const sign = off < 0 ? "før" : "etter";
    const abs = Math.abs(off);
    const hours = Math.floor(abs / 60);
    const mins = abs % 60;
    const human =
      hours && mins ? `${hours}t ${mins}m`
        : hours ? `${hours}t`
          : `${mins}m`;
    return `${human} ${sign} ${rule.scheduleAnchor || "?"}`;
  }
  return "—";
}

function describeAction(rule) {
  const base = ACTION_LABELS[rule.actionType] || rule.actionType;
  const cfg = rule.actionConfig || {};
  if (rule.actionType === "send_email" && cfg.emailType) {
    return `${base} · ${cfg.emailType}`;
  }
  if (rule.actionType === "push_to_wc" && cfg.kind) {
    return `${base} · ${cfg.kind}`;
  }
  return base;
}

function AutomationRules() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [triggerFilter, setTriggerFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [enabledFilter, setEnabledFilter] = useState("");
  const [search, setSearch] = useState("");

  const [toggling, setToggling] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(null); // rule object
  const [confirmDisableInput, setConfirmDisableInput] = useState("");

  const limit = 25;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const filterPayload = useMemo(() => {
    const p = { limit, page_number: page };
    if (triggerFilter) p.trigger_type = triggerFilter;
    if (actionFilter) p.action_type = actionFilter;
    if (enabledFilter) p.enabled = enabledFilter;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [page, triggerFilter, actionFilter, enabledFilter, search]);

  async function fetchData() {
    setLoading(true);
    setError("");
    try {
      const res = await getAutomationRules(filterPayload);
      if (res?.ok) {
        setRules(res.rules || []);
        setTotal(Number(res.total) || 0);
      } else {
        setError(res?.message || res?.error || "Kunne ikke hente regler");
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  // Refetch when filter or page changes.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, triggerFilter, actionFilter, enabledFilter]);

  function applyFilters() {
    setPage(1);
    fetchData();
  }

  function resetFilters() {
    setTriggerFilter("");
    setActionFilter("");
    setEnabledFilter("");
    setSearch("");
    setPage(1);
  }

  async function handleToggle(rule, nextEnabled) {
    // System rules require an explicit confirmation when being disabled.
    if (rule.isSystem && !nextEnabled) {
      setConfirmDisable(rule);
      setConfirmDisableInput("");
      return;
    }
    await applyToggle(rule, nextEnabled);
  }

  async function applyToggle(rule, nextEnabled) {
    setToggling(rule.id);
    try {
      const res = await toggleAutomationRule(rule.id, !!nextEnabled);
      if (res?.ok) {
        notifyCenter("success", nextEnabled ? "Aktivert" : "Deaktivert");
        setRules((prev) =>
          prev.map((r) => (r.id === rule.id ? { ...r, enabled: !!nextEnabled } : r)),
        );
      } else {
        notifyCenter("error", res?.message || res?.error || "Toggling feilet");
      }
    } catch (e) {
      notifyCenter("error", e?.message || "Toggling feilet");
    } finally {
      setToggling(null);
      setConfirmDisable(null);
      setConfirmDisableInput("");
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteAutomationRule(deleteTarget.id);
      if (res?.ok) {
        notifyCenter("success", "Slettet");
        setDeleteTarget(null);
        fetchData();
      } else {
        notifyCenter("error", res?.message || res?.error || "Sletting feilet");
      }
    } catch (e) {
      notifyCenter("error", e?.message || "Sletting feilet");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <DashboardLayout>
      <SoftBox py={3} px={{ xs: 1, md: 2 }}>
        <Card>
          {/* Header */}
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            p={3}
          >
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="bold">
                Automatiseringer
              </SoftTypography>
              <SoftTypography variant="button" color="text">
                Hva skal kjøre, og når? — regler for e-post, vendor-push og WC-synk.
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <Tooltip title="Oppdater">
                <IconButton
                  onClick={fetchData}
                  disabled={loading}
                  sx={(t) => ({
                    border: `1px solid ${t.palette.grey[300]}`,
                    borderRadius: "10px",
                  })}
                >
                  {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => navigate("/automation-rules/new")}
              >
                + Ny regel
              </SoftButton>
            </SoftBox>
          </SoftBox>

          <Divider sx={{ my: 0 }} />

          {/* Filters */}
          <SoftBox p={3}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Trigger-type
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={triggerFilter}
                    displayEmpty
                    onChange={(e) => {
                      setTriggerFilter(e.target.value);
                      setPage(1);
                    }}
                    sx={SELECT_SX}
                  >
                    <MenuItem value="">Alle triggere</MenuItem>
                    <MenuItem value="event">Event</MenuItem>
                    <MenuItem value="schedule">Tidsplan</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Handlingstype
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={actionFilter}
                    displayEmpty
                    onChange={(e) => {
                      setActionFilter(e.target.value);
                      setPage(1);
                    }}
                    sx={SELECT_SX}
                  >
                    <MenuItem value="">Alle handlinger</MenuItem>
                    <MenuItem value="send_email">E-post</MenuItem>
                    <MenuItem value="send_vendor_push">Vendor-push</MenuItem>
                    <MenuItem value="push_to_wc">Push til WC</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Status
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={enabledFilter}
                    displayEmpty
                    onChange={(e) => {
                      setEnabledFilter(e.target.value);
                      setPage(1);
                    }}
                    sx={SELECT_SX}
                  >
                    <MenuItem value="">Alle</MenuItem>
                    <MenuItem value="true">Aktivert</MenuItem>
                    <MenuItem value="false">Deaktivert</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={4}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Søk
                </SoftTypography>
                <SoftInput
                  placeholder="Navn eller event-type"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                />
              </Grid>
            </Grid>
            <SoftBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <SoftButton variant="outlined" color="secondary" size="small" onClick={resetFilters}>
                Nullstill
              </SoftButton>
              <SoftButton variant="gradient" color="info" size="small" onClick={applyFilters}>
                Bruk filter
              </SoftButton>
            </SoftBox>
          </SoftBox>

          <Divider sx={{ my: 0 }} />

          {error && (
            <SoftBox px={3} py={2}>
              <SoftTypography variant="button" color="error">
                {error}
              </SoftTypography>
            </SoftBox>
          )}

          {/* Table */}
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "26%" }}>Navn</TableCell>
                  <TableCell sx={{ width: "10%" }}>Trigger</TableCell>
                  <TableCell sx={{ width: "22%" }}>Detaljer</TableCell>
                  <TableCell sx={{ width: "18%" }}>Handling</TableCell>
                  <TableCell sx={{ width: "8%" }} align="center">Aktivert</TableCell>
                  <TableCell sx={{ width: "10%" }}>Sist kjørt</TableCell>
                  <TableCell sx={{ width: "6%" }} align="center">Handlinger</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <SoftBox>
                        <SoftTypography variant="body2" color="secondary" mb={2}>
                          Ingen regler funnet
                        </SoftTypography>
                        <SoftButton
                          variant="gradient"
                          color="info"
                          size="small"
                          onClick={() => navigate("/automation-rules/new")}
                        >
                          + Opprett den første
                        </SoftButton>
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                )}
                {rules.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    onClick={() => navigate(`/automation-rules/edit/${r.id}`)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <SoftBox display="flex" alignItems="center" gap={1}>
                        {r.isSystem && (
                          <Tooltip title="Systemregel — kan ikke slettes">
                            <LockOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                          </Tooltip>
                        )}
                        <SoftTypography variant="button" fontWeight="medium" sx={{ fontSize: 13, textTransform: "none" }}>
                          {r.name}
                        </SoftTypography>
                      </SoftBox>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={TRIGGER_LABELS[r.triggerType] || r.triggerType}
                        size="small"
                        color={r.triggerType === "schedule" ? "warning" : "primary"}
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                    </TableCell>
                    <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
                        {describeTrigger(r)}
                      </SoftTypography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={describeAction(r)}
                        size="small"
                        color={ACTION_COLORS[r.actionType] || "default"}
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                      {Number(r.delayMinutes) > 0 && (
                        <Chip
                          label={`+${r.delayMinutes}m`}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, height: 22, ml: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={!!r.enabled}
                        disabled={toggling === r.id}
                        onChange={(e) => handleToggle(r, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <SoftTypography variant="caption" color="text">
                        {formatDateTime(r.lastFiredAt)}
                      </SoftTypography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Rediger">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/automation-rules/edit/${r.id}`)}
                          sx={{ color: "info.main" }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!r.isSystem && (
                        <Tooltip title="Slett">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteTarget(r)}
                            sx={{ color: "error.main" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer pagination */}
          <Divider sx={{ my: 0 }} />
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            p={3}
          >
            <SoftTypography variant="caption" color="secondary">
              Viser {rules.length ? (page - 1) * limit + 1 : 0}
              {"–"}
              {(page - 1) * limit + rules.length} av {total}
            </SoftTypography>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <SoftButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
              >
                Forrige
              </SoftButton>
              <SoftTypography variant="caption" color="text" px={1}>
                Side {page} / {totalPages}
              </SoftTypography>
              <SoftButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages || loading}
              >
                Neste
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />

      {/* Confirm-delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Slette regel?</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text">
            Vil du slette regelen <strong>{deleteTarget?.name}</strong>? Denne handlingen
            kan ikke angres.
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <SoftButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDeleteTarget(null)}
          >
            Avbryt
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="error"
            size="small"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={16} /> : "Slett"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* Confirm-disable-system-rule dialog */}
      <Dialog
        open={!!confirmDisable}
        onClose={() => setConfirmDisable(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Deaktivere systemregel?</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text" mb={2}>
            Du er i ferd med å deaktivere systemregelen{" "}
            <strong>{confirmDisable?.name}</strong>. Dette vil stoppe automatisk{" "}
            {ACTION_LABELS[confirmDisable?.actionType] || "handling"} for{" "}
            <code>{confirmDisable?.eventType || confirmDisable?.scheduleAnchor}</code>.
          </SoftTypography>
          <SoftTypography variant="caption" color="text" mb={1} display="block">
            Skriv <strong>DEAKTIVER</strong> for å bekrefte:
          </SoftTypography>
          <SoftInput
            placeholder="DEAKTIVER"
            value={confirmDisableInput}
            onChange={(e) => setConfirmDisableInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <SoftButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => {
              setConfirmDisable(null);
              setConfirmDisableInput("");
            }}
          >
            Avbryt
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="warning"
            size="small"
            disabled={confirmDisableInput !== "DEAKTIVER" || toggling === confirmDisable?.id}
            onClick={() => applyToggle(confirmDisable, false)}
          >
            {toggling === confirmDisable?.id ? <CircularProgress size={16} /> : "Deaktiver"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default AutomationRules;
